/**
 * The promoter portal's copy. It lives ONLY here, never in src/i18n/locales or admin/src/locales: the portal
 * and panel bundles must not carry it, and this bundle carries neither of theirs (scripts/check-bundle-split.mjs,
 * rule 8). One file per screen family and language; every file is `{ "promoter": { ... } }`, deep-merged here
 * into the one `promoter` namespace. A leaf two files both define is a collision the locale test refuses.
 */
export type Tree = { [k: string]: string | Tree };

const EN = import.meta.glob<{ default: Tree }>('./en/*.json', { eager: true });
const ES = import.meta.glob<{ default: Tree }>('./es/*.json', { eager: true });

function isTree(v: unknown): v is Tree {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function mergeInto(target: Tree, source: Tree, path: string, collisions: string[]): void {
  for (const [k, v] of Object.entries(source)) {
    const key = path ? `${path}.${k}` : k;
    const current = target[k];
    if (isTree(v) && (current === undefined || isTree(current))) {
      const next: Tree = isTree(current) ? current : {};
      target[k] = next;
      mergeInto(next, v, key, collisions);
    } else if (current === undefined) {
      target[k] = v;
    } else {
      collisions.push(key);
    }
  }
}

/** The per-screen files of one language merged, plus the leaves that collided (should be none). */
export function mergeLocaleFiles(files: Record<string, { default: Tree }>): { merged: Tree; collisions: string[] } {
  const merged: Tree = {};
  const collisions: string[] = [];
  for (const name of Object.keys(files).sort()) mergeInto(merged, files[name]!.default, '', collisions);
  return { merged, collisions };
}

export const PARTNER_LOCALE_FILES = { en: EN, es: ES } as const;

export const PARTNER_LOCALES: { en: Tree; es: Tree } = {
  en: mergeLocaleFiles(EN).merged,
  es: mergeLocaleFiles(ES).merged,
};
